import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldAlert, RotateCcw } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";

const STATUS = {
  OPEN: { label: "Ouvert", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  RESOLVED: { label: "Résolu (remboursé)", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  REJECTED: { label: "Rejeté", cls: "text-zinc-400 bg-zinc-800 border-zinc-700" },
};

export default function AdminDisputes() {
  const [disputes, setDisputes] = useState(null);
  const [filter, setFilter] = useState("");
  const [panel, setPanel] = useState(null); // dispute being resolved
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => api.get(`/admin/disputes${filter ? `?status=${filter}` : ""}`).then((r) => setDisputes(r.data.disputes)).catch(() => setDisputes([])), [filter]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (d, action) => {
    try {
      await api.post(`/admin/disputes/${d.id}/resolve`, { action, amount: action === "REFUND_PARTIAL" ? Number(amount) : undefined, note });
      toast.success(action === "REJECT" ? "Litige rejeté" : "Remboursement effectué");
      setPanel(null); setAmount(""); setNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-disputes">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold flex items-center gap-2"><ShieldAlert className="w-6 h-6 text-amber-400" />Litiges</h1>
          <p className="text-sm text-zinc-500 mt-1">Gère les réclamations clients et rembourse tout ou partie d'une commande.</p>
        </div>
        <div className="flex gap-1.5">
          {[["", "Tous"], ["OPEN", "Ouverts"], ["RESOLVED", "Résolus"], ["REJECTED", "Rejetés"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`dispute-filter-${k || "all"}`}
              className={`px-3 h-8 rounded-lg text-xs border ${filter === k ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{l}</button>
          ))}
        </div>
      </div>

      {disputes === null ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div> :
        disputes.length === 0 ? <p className="text-sm text-zinc-500 py-10 text-center" data-testid="disputes-empty">Aucun litige.</p> : (
          <div className="space-y-3">
            {disputes.map((d) => {
              const st = STATUS[d.status] || STATUS.OPEN;
              return (
                <div key={d.id} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid={`dispute-${d.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{d.order_title || "Commande"}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">Client : {d.client_name} · Pro : {d.pro_name} · {d.amount?.toFixed(2)} €</p>
                    </div>
                    <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${st.cls}`}>{st.label}</span>
                  </div>
                  <p className="text-sm text-zinc-300 mt-3 whitespace-pre-line border-l-2 border-zinc-700 pl-3">{d.reason}</p>
                  {d.resolution && <p className="text-xs text-zinc-500 mt-2">Décision : {d.resolution}{d.refund_amount ? ` · ${d.refund_amount.toFixed(2)} € remboursés` : ""}{d.admin_note ? ` · ${d.admin_note}` : ""}</p>}

                  {d.status === "OPEN" && (
                    panel === d.id ? (
                      <div className="mt-3 border-t border-zinc-800 pt-3 space-y-2" data-testid={`dispute-panel-${d.id}`}>
                        <input value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="dispute-amount" type="number" placeholder="Montant partiel € (optionnel)" className="w-full h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
                        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="dispute-note" placeholder="Note interne (optionnel)" className="w-full h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => resolve(d, "REFUND_FULL")} data-testid={`dispute-refund-full-${d.id}`} className="text-xs px-3 h-9 rounded-lg gold-gradient text-black font-semibold inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" />Rembourser tout ({d.amount?.toFixed(2)} €)</button>
                          <button onClick={() => resolve(d, "REFUND_PARTIAL")} data-testid={`dispute-refund-partial-${d.id}`} className="text-xs px-3 h-9 rounded-lg border border-amber-500/40 text-amber-400">Rembourser le montant saisi</button>
                          <button onClick={() => resolve(d, "REJECT")} data-testid={`dispute-reject-${d.id}`} className="text-xs px-3 h-9 rounded-lg border border-zinc-700 text-zinc-300">Rejeter le litige</button>
                          <button onClick={() => setPanel(null)} className="text-xs px-3 h-9 rounded-lg text-zinc-500">Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setPanel(d.id); setAmount(""); setNote(""); }} data-testid={`dispute-manage-${d.id}`} className="mt-3 text-xs px-3 h-8 rounded-lg gold-gradient text-black font-semibold">Traiter le litige</button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
